import { HttpBackend, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { dropNullUndefined } from '@app/core/functions/drop-null-undefined';
import { SlicePipe } from '@app/shared/pipes/slice.pipe';
import * as _ from 'lodash';
import * as moment from 'moment';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, first, map, mapTo, switchMap, take } from 'rxjs/operators';
import {
  DiagnosisVo2,
  EncounterHeaderVoPlaceOfService,
  EncounterLineItemVo,
  EncounterStatusVo,
  EncounterTemplateVo,
  EncounterVo,
  PatientEventVo,
  ProviderConfigurationVo,
  RestApiResultOfBoolean,
  RestApiResultOfCompleteEncounterResult,
  RestApiResultOfEncounterVo,
  RestApiResultOfGuid,
  RestApiResultOfString,
  TemplateSummaryVo,
} from '../api-client.service';
import { ApplicationInsightsService } from '../application-insights.service';
import { ConfigService } from '../config.service';
import { MerakiAuthService } from '../meraki-auth.service';
import { ERROR_CODE } from '../meraki-models/error-models';
import { InvoiceTemplate, TemplateLine } from '../meraki-models/invoice-template.models';
import {
  BaseInvoice,
  INVOICE_SUBTYPE,
  INVOICE_TYPE,
  InvoiceLine,
  MEDICINE_TYPE,
  PLACE_OF_SERVICE,
  SaveInvoiceRequest,
} from '../meraki-models/invoice.models';
import {
  convertEncounterToFirestore,
  convertFirestoreToEncounter,
  convertFirestoreToPrescription,
  fromTimestamp,
} from '../meraki-models/meraki-model-util';
import { NexResponse, PriceResponse, SinglePriceRequest, TariffPriceRequest } from '../meraki-models/meraki-models';
import { DispenseResponse } from '../meraki-models/stock.models';
import { HttpClientMeraki, MERAKI_API_GATEWAY, MERAKI_CACHE_LB } from '../meraki-nexus.providers';
import { EncounterType } from '../state/clinical-encounter/clinical-encounter.model';

const PRACTICE_ROOT = 'ClinicalPractice';
const PROVIDER_ROOT = 'ClinicalProvider';
const ENCOUNTER_ROOT = 'Encounter';

@Injectable({
  providedIn: 'root',
})
export class MerakiEncounterService {
  private http: HttpClientMeraki;
  private baseUrl: string;
  private loadBalancerUrl: string;

  constructor(
    private httpHandler: HttpBackend, // Bypass Auth interceptor since it overrides with florence auth token.
    @Inject(HttpClientMeraki) http: HttpClientMeraki,
    @Inject(MERAKI_API_GATEWAY) baseUrl: string,
    @Inject(MERAKI_CACHE_LB) cacheLbUrl: string,
    private authService: MerakiAuthService,
    private firestore: AngularFirestore,
    private slicePipe: SlicePipe,
    private configService: ConfigService,
    private appInsightService: ApplicationInsightsService
  ) {
    this.http = http;
    //this.http = new HttpClient(httpHandler);

    this.baseUrl = baseUrl ? baseUrl : '';
    this.loadBalancerUrl = cacheLbUrl;
  }

  searchProcedure(
    query: string = '',
    year: number = new Date().getFullYear(),
    type: string = '10',
    page: number = 0,
    pageLimit: number = 10
  ): Observable<NexResponse<any>> {
    // todo speciality code is missing
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .post<NexResponse<any>>(
            `${this.baseUrl}/v1/medicine/search/searchProcedure`,
            {
              query,
              year,
              field: 'Code',
              type,
              page,
              pageLimit,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .pipe(
            // TODO: Standardize Nexus function error handling. Interceptor on the custom httpclient maybe?
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return of(httpError.error);
              }
              return throwError(httpError);
            })
          )
      )
    );
  }

  createTemplateGroup(bpn: string, hpcsaNo: string, templateType: string, request: TemplateSummaryVo) {
    return from(
      this.firestore
        .collection('Practice')
        .doc(bpn)
        .collection('Provider')
        .doc(hpcsaNo)
        .collection('InvoiceTemplate')
        .doc()
        .set({
          Caption: request.Caption,
          Description: request.Description,
          IsGroup: true,
          IsActive: true,
          SortOrder: 0,
          Type: templateType == 'Template' ? 'Invoice' : templateType,
        } as InvoiceTemplate)
    );
  }

  deleteTemplate(bpn: string | null | undefined, hpcsaNo: string | null | undefined, xref: string): Observable<RestApiResultOfBoolean> {
    if (!bpn || !hpcsaNo) {
      return of({ Sucess: false, Data: false });
    }
    const templateRef = this.firestore.collection('Practice').doc(bpn).collection('Provider').doc(hpcsaNo).collection('InvoiceTemplate');

    return from(templateRef.doc(xref).set({ IsActive: false }, { merge: true })).pipe(
      mapTo({
        Sucess: true,
        Data: true,
      })
    );
  }

  deleteTemplateFromGroup(
    bpn: string | null | undefined,
    hpcsaNo: string | null | undefined,
    groupXRef: string,
    xref: string
  ): Observable<RestApiResultOfBoolean> {
    if (!bpn || !hpcsaNo) {
      return of({ Sucess: false, Data: false });
    }
    const templateRef = this.firestore.collection('Practice').doc(bpn).collection('Provider').doc(hpcsaNo).collection('InvoiceTemplate');

    return templateRef
      .doc(groupXRef)
      .valueChanges()
      .pipe(
        take(1),
        switchMap(group => {
          const templates = group?.Templates || [];
          // group members may or may not carry an Id, so match the same way TemplateXRef is derived (Id || Caption)
          const remaining = templates.filter(s => (s.Id || s.Caption) != xref);
          if (remaining.length === templates.length) {
            return of({ Sucess: false, Data: false });
          }
          return from(templateRef.doc(groupXRef).set({ ...group, Templates: remaining }, { merge: true })).pipe(
            mapTo({ Sucess: true, Data: true })
          );
        }),
        catchError(err => {
          console.error('saving error', err);
          return of({ Sucess: false, Data: false });
        })
      );
  }

  updateTemplate(
    bpn: string,
    hpcsaNo: string,
    template: EncounterTemplateVo,
    existingTemplates: TemplateSummaryVo[]
  ): Observable<RestApiResultOfString> {
    const request = dropNullUndefined(this.fromEncounterTemplate(template));

    const templateRef = this.firestore.collection('Practice').doc(bpn).collection('Provider').doc(hpcsaNo).collection('InvoiceTemplate');
    // adding to group if caption contains '|'
    // (following old drapp communication principle to not refactor other components)

    if (template.Caption.includes('|')) {
      const groupRef = template.Caption.split('|')[0];
      const caption = template.Caption.split('|')[1];

      if (
        !template.TemplateXref &&
        existingTemplates.filter(s => s.Caption == caption && s.GroupInvoiceTemplateXRef == groupRef).length > 0
      ) {
        return of({
          Data: '',
          Sucess: false,
          ResponseMessage: 'Name of favorite is DUPLICATE',
        } as RestApiResultOfString);
      }

      return templateRef
        .doc(groupRef)
        .valueChanges()
        .pipe(
          take(1),
          switchMap(group =>
            from(
              templateRef.doc(groupRef).set(
                {
                  ...group,
                  Templates: [
                    ...(group?.Templates || []),
                    {
                      ...request,
                      Caption: template.Caption.split('|')[1],
                    },
                  ],
                },
                { merge: true }
              )
            )
          ),
          mapTo({ Sucess: true }),
          catchError(err => {
            console.error('saving error', err);
            return of(null);
          })
        );
    }

    if (
      !template.TemplateXref &&
      existingTemplates.filter(s => s.Caption == template.Caption && s.GroupInvoiceTemplateXRef == null).length > 0
    ) {
      return of({ Data: '', Sucess: false, ResponseMessage: 'Name of favorite is DUPLICATE' } as RestApiResultOfString);
    }

    return (
      template.TemplateXref
        ? from(templateRef.doc(template.TemplateXref).set(request, { merge: true }))
        : from(templateRef.doc().set(request))
    ).pipe(mapTo({ Sucess: true }));
  }

  getInvoiceTemplateGroup(bpn: string, hpcsaNo: string, templateType: string): Observable<TemplateSummaryVo[]> {
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('Provider')
      .doc(hpcsaNo)
      .collection('InvoiceTemplate', r => r.where('Type', '==', templateType).where('IsGroup', '==', true).where('IsActive', '==', true))
      .valueChanges<string>({ idField: 'Id' })
      .pipe(
        //take(1), // todo confirm if easy to make real time
        map(templates =>
          templates.map(
            (t: InvoiceTemplate) =>
              ({
                Caption: t.Caption,
                Code: t.Description, // confirm
                Description: t.Description,
                SortOrder: t.SortOrder,
                TemplateType: templateType,
                TemplateXRef: t.Id || t.Caption,
                GroupInvoiceTemplateXRef: t.Id,
              }) as TemplateSummaryVo
          )
        ),
        map(list => _.chain(list).orderBy('Caption', 'asc').value())
      );
  }

  getInvoiceTemplateGroupContent(bpn: string, hpcsaNo: string, templateType: string, groupXref: string): Observable<TemplateSummaryVo[]> {
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('Provider')
      .doc(hpcsaNo)
      .collection('InvoiceTemplate')
      .doc(groupXref)
      .get()
      .pipe(
        take(1),
        map(s => s.data() as InvoiceTemplate),
        map(s =>
          s.Templates.map(
            t =>
              ({
                Caption: t.Caption,
                Code: t.Description, // confirm
                Description: t.Description,
                SortOrder: t.SortOrder,
                TemplateType: templateType,
                TemplateXRef: t.Id || t.Caption,
                GroupInvoiceTemplateXRef: groupXref,
                OriginalTemplate: t,
              }) as TemplateSummaryVo
          )
        )
      );
  }

  getInvoiceTemplates(
    bpn: string,
    hpcsaNo,
    templateType: string,
    pageSize: number,
    pageNo: number,
    search: string
  ): Observable<EncounterTemplateVo[]> {
    // adjust type to not overcomplicate tweaking logic on higher level
    let type = templateType;
    if (templateType == 'Template') {
      type = 'Invoice';
    }
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('Provider')
      .doc(hpcsaNo)
      .collection('InvoiceTemplate', r => r.where('Type', '==', type).where('IsActive', '==', true))
      .valueChanges<string>({ idField: 'Id' })
      .pipe(
        //take(1), // todo we can't set otherwise only load local cache version and no server call
        // loose templates only — group members load via their category and don't belong in page slots
        map((templates: InvoiceTemplate[]) =>
          templates.filter(template => !template.IsGroup).map(template => this.toEncounterTemplate(template, templateType))
        ),
        map(templates => (search && templates.filter(t => t.Caption?.toLocaleLowerCase().includes(search.toLowerCase()))) || templates),
        // sort before slicing — firestore returns doc-id order, which makes pages arbitrary
        map(templates => _.orderBy(templates, t => t.Caption?.toLocaleLowerCase() || '', 'asc')),
        map(templates => templates.slice(pageNo * pageSize, pageNo * pageSize + pageSize))
      );
  }

  private fromEncounterTemplate(template: EncounterTemplateVo): InvoiceTemplate {
    return {
      Caption: template.Caption,
      Description: template.Description,
      IsGroup: false,
      IsActive: true,
      SortOrder: 0,
      Type: template.TemplateType == 'MyTemplate' ? 'Invoice' : template.TemplateType,
      Diagnosis: template.EncounterLineItems.EncounterHeader.Diagnosis.map((s, index) => ({
        Code: s.DiagnosisCode,
        Description: s.DiagnosisDescription,
        OrderNo: index,
      })),
      Lines: [
        ...template.EncounterLineItems.LineItems.map(
          s =>
            ({
              Diagnoses: s.Diagnosis.map((d, index) => ({
                Code: d.DiagnosisCode,
                Description: d.DiagnosisDescription,
                OrderNo: index,
              })),
              ChargeCode: s.ChargeCode,
              Description: s.ChargeDesc,
              Quantity: s.ChargeQuan,
              LineType: s.LineType,
              LineNumber: s.LineNum,
              NappiCode: s.NappiCode,
              DosageType: s.DosageType,
              FrequencyType: s.FrequencyType,
              DurationType: s.DurationType,
              DurationUnit: s.DurationUnit,
              DosageUnit: s.DosageUnit,
              PeriodType: s.PeriodType,
              PeriodUnit: s.PeriodUnit,
              Repeat: s.Repeat,
              ChronicIndicator: s.ChronicIndicator,
              Route: s.Parameters?.RouteOfAdministration,
              FrequencyUnit: s.FrequencyUnit,
              AdditionalInstructions: s.Parameters?.InformationText,
              Instructions: s.Parameters?.AdditionalInstructionSelections?.split(', '),
              // new field in model, forge doesn't know about it
              DosageDescription: s.Parameters?.DosageDescription,
              Dispense: s.LineType == 'Medicine',
            }) as TemplateLine
        ),
        ...template.MedicationPrescriptionLines.map(
          s =>
            ({
              Diagnoses: s.Diagnosis.map((d, index) => ({
                Code: d.DiagnosisCode,
                Description: d.DiagnosisDescription,
                OrderNo: index,
              })),
              LineType: 'Medicine',
              Description: s.MedicationDescription,
              MedicationDescription: s.MedicationDescription,
              Dispense: false,
              Quantity: s.Quantity,
              LineNumber: s.LineNum,
              NappiCode: s.NappiCode,
              DosageType: s.DosageType,
              FrequencyUnit: s.FrequencyUnits,
              DurationType: s.DurationType,
              DurationUnit: s.DurationUnit,
              DosageUnit: s.DosageUnits,
              PeriodType: s.PeriodType,
              PeriodUnit: s.PeriodUnit,
              Repeat: s.Repeat,
              ChronicIndicator: s.ChronicIndicator,
              DispensingInstructions: s.DispensingInstructions,
              AdditionalInstructions: s.Parameters?.InformationText,
              Instructions: s.Parameters?.AdditionalInstructionSelections?.split(', '),
              // new field in model, forge doesn't know about it
              DosageDescription: s.Parameters?.DosageDescription,
            }) as TemplateLine
        ),
      ],
      // (AD) prescriptions model of template service NOT used here same as via mymps integration
      // instead used Dispense flag to diff between dispense/prescribe so then we could quickly prescribe dispensed med and vice versa
      /* Prescriptions: template.MedicationPrescriptionLines.map(s => ({
          Description: s.Description,
          Quantity: s.Quantity,
          LineNumber: s.LineNum,
          NappiCode: s.NappiCode,
          DosageType: s.DosageType,
          FrequencyUnit: s.FrequencyUnits,
          DurationType: s.DurationType,
          DurationUnit: s.DurationUnit,
          DosageUnit: s.DosageUnits,
          PeriodType: s.PeriodType,
          PeriodUnit: s.PeriodUnit,
          Repeat: s.Repeat,
          ChronicIndicator: s.ChronicIndicator,
          DispensingInstructions: s.DispensingInstructions,
          MedicationDescription: s.MedicationDescription,
        } as InvoicePrescription)) */
    } as InvoiceTemplate;
  }

  toEncounterTemplate(t: InvoiceTemplate, templateType: string, groupId?: string): EncounterTemplateVo {
    return {
      Caption: t.Caption,
      Code: t.Description,
      Description: t.Description,
      SortOrder: t.SortOrder,
      TemplateType: templateType,
      TemplateXref: t.Id || t.Caption,
      TemplateId: t.Id || t.Caption, // todo templates in group doesn't have id
      GroupInvoiceTemplateXRef: groupId,
      EncounterLineItems: {
        EncounterHeader: {
          Diagnosis:
            t.Diagnosis?.map(
              d =>
                ({
                  DiagnosisCode: d.Code,
                  DiagnosisDescription: d.Description,
                }) as DiagnosisVo2
            ) || [],
        },
        LineItems: (t.Lines ?? []).map(
          (s: TemplateLine | any) =>
            ({
              ChargeCode: s.ChargeCode,
              ChargeDesc: s.Description,
              ChargeQuan: s.Quantity,
              LineType: s.LineType,
              LineNum: s.LineNumber,
              NappiCode: s.NappiCode,
              DosageType: s.DosageType || s.UserDefinedDosageType,
              FrequencyType: null, //todo
              DurationType: s.DurationType,
              DurationUnit: s.DurationUnit,
              DosageUnit: s.DosageUnit,
              PeriodType: s.PeriodType,
              PeriodUnit: s.PeriodUnit,
              Repeat: s.Repeat,
              ChronicIndicator: s.ChronicIndicator,
              FrequencyUnit: s.FrequencyUnit,
              Amount: s.TotalIncVat,
              Parameters:
                (s.LineType == 'Medicine' && {
                  RouteOfAdministration: s.Route,
                  AdditionalInstructionSelections: s.AdditionalInstructionSelections || '',
                  InformationText: s.AdditionalInstructions,
                  DosageDescription: s.DosageDescription,
                  MedicineType: s.Dispense ? 'Dispense' : 'Prescribe',
                }) ||
                null,
            }) as EncounterLineItemVo
        ),
      },
    } as EncounterTemplateVo;
  }

  priceEncounter(
    practiceid: string,
    encounterId: string,
    encounterVo: EncounterVo,
    provider: ProviderConfigurationVo,
    onlyMissingPrices: boolean = false
  ): Observable<RestApiResultOfEncounterVo> {
    // todo private rate is not part of
    /** 0 = Uknown, 2 = Telehealth, 11 = ConsultingRooms, 21 = InPatientHospital, 24 = DayClinic */
    const placeOfServiceCode = encounterVo.EncounterLineItems.EncounterHeader.PlaceOfService;
    let placeOfService = 'OH';
    if (placeOfServiceCode == EncounterHeaderVoPlaceOfService._21) {
      placeOfService = 'IH';
    } else if (placeOfServiceCode == EncounterHeaderVoPlaceOfService._24) {
      placeOfService = 'DC';
    }
    const linesForPricing = encounterVo.EncounterLineItems.LineItems.filter(s => s.PriceOverride != true).filter(
      s => (onlyMissingPrices && (s.Amount == 0 || !s.Amount)) || !onlyMissingPrices
    );
    return forkJoin([
      ...linesForPricing.map(line => {
        const year = moment(encounterVo.EncounterLineItems.EncounterHeader.DateOfService).year().toString();
        const optionCode = encounterVo.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidOptionCode;

        if (line.LineType === 'Procedure' && line.ChargeCode) {
          const request = {
            type: 'Procedure',
            placeOfService: placeOfService,
            date: encounterVo.EncounterLineItems.EncounterHeader.DateOfService,
            disciplineCode: provider.SpecialityCode,
            planOptionCode: optionCode,
            priceCode: line.ChargeCode,
            practiceId: provider.PracticeNumber,
            providerId: provider.HPCSANumber,
            quantity: line.ChargeQuan,
            isMedicalAid: !encounterVo.PatientEventDetails.Patient.PatientAccountDetails.IsCashAccount,
          } as SinglePriceRequest;
          return this.getSinglePrice(request).pipe(
            map(s => this.addPricingFields(line, s)),
            catchError(err =>
              throwError(`Unable to price ${line.LineType}: ${line.ChargeCode}, ${this.slicePipe.transform(line.ChargeDesc, 0, 70)} `)
            )
          );
        }
        if ((line.LineType === 'Medicine' || line.LineType === 'Consumable') && line.NappiCode) {
          const request = {
            type: line.LineType,
            placeOfService: placeOfService,
            date: encounterVo.EncounterLineItems.EncounterHeader.DateOfService,
            disciplineCode: provider.SpecialityCode,
            planOptionCode: optionCode,
            priceCode: line.NappiCode,
            practiceId: provider.PracticeNumber,
            providerId: provider.HPCSANumber,
            quantity: line.ChargeQuan,
            isMedicalAid: !encounterVo.PatientEventDetails.Patient.PatientAccountDetails.IsCashAccount,
          } as SinglePriceRequest;

          return this.getSinglePrice(request).pipe(
            map(s => this.addPricingFields(line, s)),
            catchError(err =>
              throwError(`Unable to price ${line.LineType}: ${line.NappiCode}, ${this.slicePipe.transform(line.ChargeDesc, 0, 70)} `)
            )
          );
        }
        return of(line);
      }),
    ]).pipe(
      map(lines => ({
        Sucess: true,
        Data: {
          ...encounterVo,
          EncounterLineItems: {
            ...encounterVo.EncounterLineItems,
            LineItems: lines,
          },
        },
      })),
      catchError(err => {
        return of({
          Sucess: false,
          ResponseCode: 400,
          ResponseMessage: err,
        });
      })
    );
  }

  addPricingFields(line: EncounterLineItemVo, s: NexResponse<PriceResponse>): EncounterLineItemVo {
    if (!s.data.payload.result.isSuccess) {
      throw new Error(`Unable to price ${line.LineType} line item: ${line.ChargeCode}`);
    }

    const totalPrice = parseFloat(Number(s.data.payload.result.price).toFixed(2));

    return {
      ...line,
      Amount: totalPrice,
      TotalIncVat: totalPrice,
      //UnitPrice: s.data.payload.result.price
    };
  }

  getSinglePrice(priceRequest: SinglePriceRequest): Observable<NexResponse<PriceResponse>> {
    const params = {
      type: priceRequest.type,
      placeOfService: priceRequest.placeOfService,
      disciplineCode: priceRequest.disciplineCode,
      planOptionCode: priceRequest.planOptionCode || null,
      priceCode: priceRequest.priceCode,
      date: priceRequest.date.toISOString(),
      isMedicalAid: String(priceRequest.isMedicalAid),
      quantity: priceRequest.quantity,
      /*isMultiBranch: String(priceRequest.isMultiBranch) */
    };
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.get<NexResponse<PriceResponse>>(
          `${this.loadBalancerUrl}/v1/pricing/single/${priceRequest.practiceId}/${priceRequest.providerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              rejectUnauthorized: 'false',
            },
            params: {
              ...dropNullUndefined(params),
            },
          }
        )
      )
    );
  }

  getTariffPrice(priceRequest: TariffPriceRequest): Observable<NexResponse<any>> {
    // todo extract load balancer (responsible for caching) to env config
    // please note atm private rates not returned by endpont

    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.get<NexResponse<any>>(`${this.loadBalancerUrl}/v1/pricing/tariff/${priceRequest.practiceId}/${priceRequest.providerId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            rejectUnauthorized: 'false',
          },
          params: {
            year: priceRequest.year,
            tariffCode: priceRequest.tariffCode,
            disciplineCode: priceRequest.disciplineCode,
            placeOfService: priceRequest.placeOfService,
            planOptionCode: priceRequest.planOptionCode,
          },
        })
      )
    );
  }

  getMedicinePrice(bpn: string, nappiCode: string, planOptionCode, quantity, year): Observable<NexResponse<any>> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.get<NexResponse<any>>(`${this.loadBalancerUrl}/v1/pricing/${bpn}/medicine`, {
          headers: {
            Authorization: `Bearer ${token}`,
            rejectUnauthorized: 'false',
          },
          params: {
            nappiCode: nappiCode,
            planOptionCode: planOptionCode,
            quantity: quantity,
            year: year,
          },
        })
      )
    );
  }

  getConsumablePrice(bpn: string, nappiCode: string, planOptionCode, quantity, year): Observable<NexResponse<any>> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.get<NexResponse<any>>(`${this.loadBalancerUrl}/v1/pricing/${bpn}/consumable`, {
          headers: {
            Authorization: `Bearer ${token}`,
            rejectUnauthorized: 'false',
          },
          params: {
            nappiCode: nappiCode,
            planOptionCode: planOptionCode,
            quantity: quantity,
            year: year,
          },
        })
      )
    );
  }

  isBillingInvoiceRequired(encounter: EncounterVo) {
    return encounter.EncounterLineItems.LineItems.length > 0;
  }

  getInvoiceType(type: string): INVOICE_TYPE {
    if (type == EncounterType.MedicalAidInvoice) {
      return INVOICE_TYPE.MEDICAL_AID;
    } else if (type == EncounterType.CashInvoice) {
      return INVOICE_TYPE.CASH;
    } else if (type == EncounterType.MedicalInsurance) {
      return INVOICE_TYPE.MEDICAL_AID;
    } else if (type == EncounterType.ZeroInvoice) {
      return INVOICE_TYPE.CASH;
    }
    //return type;
  }

  getInvoiceSubtype(type: string): INVOICE_SUBTYPE {
    if (type == EncounterType.ZeroInvoice) {
      return INVOICE_SUBTYPE.NO_CHARGE;
    } else if (type == EncounterType.MedicalInsurance) {
      return INVOICE_SUBTYPE.MEDICAL_INSURER;
    }
    return INVOICE_SUBTYPE.NONE;
  }

  getPlaceOfService(placeOfService: EncounterHeaderVoPlaceOfService): PLACE_OF_SERVICE {
    /** 0 = Uknown, 2 = Telehealth, 11 = ConsultingRooms, 21 = InPatientHospital, 24 = DayClinic */
    if (placeOfService == EncounterHeaderVoPlaceOfService._2) {
      return PLACE_OF_SERVICE.TELEMEDICINE;
    }
    if (placeOfService == EncounterHeaderVoPlaceOfService._11) {
      return PLACE_OF_SERVICE.CONSULTING_ROOM;
    }
    if (placeOfService == EncounterHeaderVoPlaceOfService._21) {
      return PLACE_OF_SERVICE.INPATIENT_HOSPITAL;
    }
    if (placeOfService == EncounterHeaderVoPlaceOfService._24) {
      return PLACE_OF_SERVICE.DAY_CLINIC_HOSPITAL;
    }
    return undefined;
  }

  updateEncounterStatus(
    practiceTenantId: string,
    encounterId: string,
    status: 'Created' | 'Updated' | 'Saved' | 'Completed' | 'Cancelled'
  ): Observable<any> {
    return from(this.encounterRef(practiceTenantId, encounterId).set({ Status: status }, { merge: true }));
  }

  completeEncounter(
    bpn: string,
    encounter: EncounterVo,
    requireManualProcessing: string[]
  ): Observable<RestApiResultOfCompleteEncounterResult> {
    if (!this.isBillingInvoiceRequired(encounter)) {
      return of({ Sucess: true, Data: { EncounterId: encounter.EncounterId } });
    }

    const dosSameAsVisit =
      moment(encounter.PatientEventDetails.CheckInTime).format('YYYY-MM-DD') ==
      moment(encounter.EncounterLineItems.EncounterHeader.DateOfService).format('YYYY-MM-DD');

    const invoice: BaseInvoice = {
      //Id: uuidV4(),
      SourceInvoiceId: encounter.EncounterId,
      Type: this.getInvoiceType(encounter.EncounterType),
      Subtype: this.getInvoiceSubtype(encounter.EncounterType),
      PlaceOfService: this.getPlaceOfService(encounter.EncounterLineItems.EncounterHeader.PlaceOfService),
      DateOfService: encounter.EncounterLineItems.EncounterHeader.DateOfService,
      AdmissionDate: encounter.EncounterLineItems.EncounterHeader.AdmissionDateTime,
      DischargeDate: encounter.EncounterLineItems.EncounterHeader.DischargeDateTime,
      AuthorizationNo: encounter.EncounterLineItems.EncounterHeader.ClaimAuthorizationNumber,
      ReferringProvider: encounter.ReferringDoctorPracticeNumber && {
        TreatingPracticeNumber: encounter.ReferringDoctorPracticeNumber,
        FullName: encounter.ReferringDoctorName,
      },
      AssistingProvider: encounter.AssistingDoctorPracticeNumber && {
        TreatingPracticeNumber: encounter.AssistingDoctorPracticeNumber,
        FullName: encounter.AssistingDoctorName,
      },
      InvoiceDate: moment().toDate(),
      IdentityNo: encounter.PatientEventDetails.Patient.PatientDetails.IdentityNo,
      Branch: encounter.BranchXRef,
      HeaderDiagnosisCodes: encounter.EncounterLineItems.EncounterHeader.Diagnosis.map(s => s.DiagnosisCode),
      // don't set linked appointment if it's visit created by us
      LinkedAppointment: encounter.PatientEventDetails.IsClinicalVisit /* || !dosSameAsVisit  */
        ? null
        : encounter.PatientEventDetails.PatientEventXRef,
      TreatingProvider: {
        TreatingPracticeNumber: encounter.Provider.TreatingDoctorPracticeNumber,
        BillingPracticeNumer: encounter.Provider.PracticeNumber,
        PracticeName: encounter.Provider.PracticeName,
        HPCSANumber: encounter.Provider.HPCSANumber,
        SpecialityCode: encounter.Provider.SpecialityCode,
      },
      PolicyNo: encounter.MedicalInsurance?.PolicyNo,
      Broker: encounter.MedicalInsurance?.Broker,
      MedicalInsurer: encounter.MedicalInsurance && {
        Name: encounter.MedicalInsurance.MedicalInsurer.Name,
        //Code: encounter.MedicalInsurance.MedicalInsurer.
        Phone: encounter.MedicalInsurance.MedicalInsurer.Phone1,
        Email: encounter.MedicalInsurance.MedicalInsurer.Email,
        Id: encounter.MedicalInsurance.MedicalInsurer.Id,
        //Url:
      },
      Account: {
        Id: encounter.PatientEventDetails.Patient.PatientAccountDetails.AccountId,
        AccountNo: encounter.PatientEventDetails.Patient.PatientAccountDetails.AccountNo,
        MemberNo: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidMembershipNumber,
        SchemeName: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidName,
        SchemeCode: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidSchemeCode,
        PlanCode: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidPlanCode,
        PlanName: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidPlan,
        OptionName: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidPlanOption,
        OptionCode: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidOptionCode,
      },
      MainMember: {
        Id: encounter.PatientEventDetails.Patient.PatientAccountDetails.MainMemberXRef,
      },
      Patient: {
        Id: encounter.PatientEventDetails.Patient.PatientXRef,
        Name: encounter.PatientEventDetails.Patient.PatientDetails.FirstName,
        Surname: encounter.PatientEventDetails.Patient.PatientDetails.Surname,
        DateOfBirth: encounter.PatientEventDetails.Patient.PatientDetails.DateOfBirth,
        IdentityNo: encounter.PatientEventDetails.Patient.PatientDetails.IdentityNo,
        FileNo: encounter.PatientEventDetails.Patient.FileNo,
        DependantCode: encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidDependentCode,
        Contact: {
          Cellphone: encounter.PatientEventDetails.Patient.PatientDetails.ContactNo,
        },
      },

      //todo add other fields
      Lines: encounter.EncounterLineItems.LineItems.map(
        s =>
          ({
            Description: s.ChargeDesc,
            LineType: s.LineType,
            LineNumber: s.LineNum,
            NappiCode: s.NappiCode,
            TariffCode: s.ChargeCode,
            Quantity: s.ChargeQuan || 1, // use default 1
            DiagnosisCodes: s.Diagnosis?.map(t => t.DiagnosisCode) || [],
            AmountBilled: encounter.EncounterType == EncounterType.ZeroInvoice ? 0 : Math.round(s.Amount * 100),
            PriceOverride: s.PriceOverride,
            //Balance
            MedicineAdditionalInfo: {
              // todo map types
              DosageType: s.DosageType,
              DosageUnit: s.DosageUnit,
              DurationType: s.DurationType,
              DurationUnit: s.DurationUnit,
              FrequencyUnit: s.FrequencyUnit,
              MedicineType: s.ChronicIndicator ? MEDICINE_TYPE.CHRONIC : MEDICINE_TYPE.ACUTE,
              PeriodType: s.PeriodType,
              PeriodUnit: s.PeriodUnit,
              Repeats: s.Repeat,
              Instructions: s.Parameters?.AdditionalInstructionSelections || '',
            },
          }) as InvoiceLine
      ),
    };

    var claimNote =
      requireManualProcessing.length > 0
        ? `${encounter.Note ?? ''} [Note: Invoice deferred for manual processing due to ${requireManualProcessing.join(', ')}]`
        : encounter.Note;

    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .post<NexResponse<{ invoiceId: string }>>(
            `${this.baseUrl}/v1/invoice/claim/saveInvoice`,
            {
              BillingPracticeNumber: bpn,
              Note: claimNote,
              Source: 'Clinical',
              Invoice: invoice,
              ProcessingContext: {
                Completed: requireManualProcessing.length == 0,
                IgnoreLinkedAppointmentOnDosMismatch: true,
              },
            } as SaveInvoiceRequest,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .pipe(
            switchMap(s => {
              if (s.success) {
                return of({ Sucess: true, Data: { EncounterId: s.data.payload.invoiceId } });
              }
              var errorMessage =
                s.error.code == ERROR_CODE.DUPLICATE_REQUEST
                  ? 'Consultation already completed. Use cancel option to close consultation.'
                  : s.error.message;

              return throwError({
                Data: '',
                Sucess: false,
                Code: s.error.code,
                ResponseMessage: errorMessage,
              } as RestApiResultOfCompleteEncounterResult);
            }), //todo ucomment
            catchError(httpError => {
              if (httpError instanceof HttpErrorResponse && httpError.error?.error) {
                return throwError(httpError.error);
              }
              return throwError(httpError);
            })
          )
      )
    );
  }

  saveEncounter(providerId: string, patientId: string, encounter: EncounterStatusVo): Observable<RestApiResultOfGuid> {
    // threshold limits: 120: SymptomQuestions, 240: SymptomQuestionAnswers, 50: EncounterLineItems.LineItems,
    let questions$ = of(null);
    let encounterForUpdate = { ...dropNullUndefined(encounter), Status: 'Saved' } as EncounterStatusVo;

    encounterForUpdate = convertEncounterToFirestore(encounterForUpdate);

    const encounterRootRef = this.encounterRef(encounterForUpdate.PracticeId, encounterForUpdate.EncounterId);

    if (encounterForUpdate.SymptomQuestions.length >= 120) {
      // symptom questions is more informational data, no need to merge
      questions$ = forkJoin([
        // due to firestore batch limit 500 split into chunks
        ..._.chunk(encounterForUpdate.SymptomQuestions, 500).map(group => {
          const batch = this.firestore.firestore.batch();
          group.forEach(item => {
            batch.set(encounterRootRef.collection('SymptomQuestions').doc(item.QuestionKey.replace('/', '_')).ref, item);
          });
          return from(batch.commit());
        }),
      ]);
      encounterForUpdate = { ...encounterForUpdate, SymptomQuestions: null };
    }

    let answers$ = of(null);
    if (encounterForUpdate.SymptomQuestionAnswers.length >= 240) {
      answers$ = forkJoin([
        // due to firestore batch limit 500 split into chunks
        ..._.chunk(encounterForUpdate.SymptomQuestionAnswers, 500).map(group => {
          const batch = this.firestore.firestore.batch();
          group.forEach(item => {
            batch.set(encounterRootRef.collection('SymptomQuestionAnswers').doc(item.QuestionKey.replace('/', '_')).ref, item);
          });
          return from(batch.commit());
        }),
      ]);
      encounterForUpdate = { ...encounterForUpdate, SymptomQuestionAnswers: null };
    }

    let prescriptions$ = of(null);
    if (encounterForUpdate.MedicationsPrescriptions?.length > 0) {
      const batch = this.firestore.firestore.batch();
      encounterForUpdate.MedicationsPrescriptions.forEach(item => {
        batch.set(encounterRootRef.collection('MedicationsPrescriptions').doc(item.MedicationPrescriptionId).ref, item);
      });
      prescriptions$ = from(batch.commit()).pipe(catchError(s => of(null)));
      encounterForUpdate = { ...encounterForUpdate, MedicationsPrescriptions: null };
    }

    let lines$ = of(null);
    if (encounterForUpdate.EncounterLineItems?.LineItems.length >= 50) {
      lines$ = forkJoin([
        // due to firestore batch limit 500 split into chunks
        ..._.chunk(encounterForUpdate.EncounterLineItems.LineItems, 500).map(group => {
          const batch = this.firestore.firestore.batch();
          group.forEach(item => {
            batch.set(
              encounterRootRef
                .collection('EncounterLineItems.LineItems')
                .doc(`${item.LineType}-${item.NappiCode || '00'}-${item.ChargeCode}`).ref,
              item
            );
          });
          return from(batch.commit());
        }),
      ]);

      encounterForUpdate = {
        ...encounterForUpdate,
        EncounterLineItems: { ...encounterForUpdate.EncounterLineItems, LineItems: [] },
        LastUpdated: moment().toDate(),
      };
    }

    return forkJoin([lines$, questions$, answers$, prescriptions$, from(encounterRootRef.set(encounterForUpdate))]).pipe(
      map(() => ({ Sucess: true }) as RestApiResultOfGuid),
      catchError(s => {
        console.warn(s);
        this.appInsightService.trackException(s, 'save-encounter', { encounterVo: JSON.stringify(encounter) });
        return of({ Sucess: false, ResponseMessage: s.message });
      })
    );
  }

  private encounterRef(practiceid: string, encounterId: string) {
    return this.firestore.collection(PRACTICE_ROOT).doc(practiceid).collection(ENCOUNTER_ROOT).doc(encounterId);
  }

  getEncounter(practiceid: string, encounterId: string) {
    return this.encounterRef(practiceid, encounterId)
      .get()
      .pipe(
        take(1),
        map(s => s.data() as EncounterVo)
      );
  }

  getEncounterPrescriptionLive$(practiceId: string, encounterId: string, prescriptionId: string) {
    return this.encounterRef(practiceId, encounterId)
      .collection('MedicationsPrescriptions')
      .doc(prescriptionId)
      .valueChanges()
      .pipe(map(s => convertFirestoreToPrescription(s)));
  }

  updateEncounterPrescriptionDischem(practiceId: string, encounterId: string, prescriptionId: string, prescription: any) {
    return from(this.encounterRef(practiceId, encounterId).collection('MedicationsPrescriptions').doc(prescriptionId).update(prescription));
  }

  getActiveEncounterForPatient(
    practiceTenantId: string,
    providerId: string,
    patientId: string,
    visit?: PatientEventVo
  ): Observable<EncounterVo> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection(ENCOUNTER_ROOT, r =>
        r.where('Status', 'in', ['Created', 'Updated', 'Saved']).where('PatientEventDetails.Patient.PatientId', '==', patientId)
      )
      .valueChanges()
      .pipe(
        take(1),
        map(encounters => encounters?.filter(e => e.PatientEventDetails.PracticeId === providerId) || []),
        map(encounters =>
          _.orderBy(encounters, [s => fromTimestamp(s.PatientEventDetails?.CheckInTime), s => s.EncounterId], ['desc', 'asc'])
        ),
        map(encounters =>
          visit != null
            ? encounters.filter(
                e =>
                  e.PatientEventDetails.PatientEventId === visit.PatientEventId ||
                  e.PatientEventDetails.PatientEventXRef === visit.PatientEventXRef
              )
            : this.onlyWithImportantInfo(encounters)
        ),
        map(encounters => (encounters.length > 0 && encounters[0]) || null),
        // load full encounter (in case if contains sub-collections)
        switchMap((encounter: EncounterStatusVo) => this.loadFullEncounter(encounter, practiceTenantId)),
        map(encounter => (encounter && convertFirestoreToEncounter(encounter)) || null)
      );
  }

  // HBC Go "Today's Consultations" — encounters at this practice whose
  // EncounterLineItems.EncounterHeader.DateOfService falls within the local calendar
  // day. DateOfService is initialised from CheckInTime at create time and re-stamped
  // to "now" at completion (see clinical-encounter.service.ts completeEncounterHbcGo$),
  // so it represents "the day the consult happened" for both in-progress and
  // finished visits — whereas CheckInTime alone misses resumed encounters (checked
  // in on a previous day, completed today), which is the common case when the
  // doctor leaves a consult open overnight.
  //
  // Returned encounters are the lightweight top-level docs (no sub-collections);
  // callers display summary fields only and do not need SymptomQuestions /
  // LineItems / Prescriptions, so we skip loadFullEncounter /
  // convertFirestoreToEncounter (the latter eagerly walks EncounterLineItems and
  // crashes on partial docs). We only normalise the timestamps used for binding
  // and sorting. Cancelled encounters are excluded; Created/Updated/Saved/Completed
  // are returned so the panel can show both in-flight and finished visits.
  getEncountersCheckedInToday$(practiceTenantId: string): Observable<EncounterStatusVo[]> {
    return this.getEncountersCheckedInLastNDays$(practiceTenantId, 1);
  }

  // Rolling N-day window variant of getEncountersCheckedInToday$. days=1 → today only;
  // days=7 → last 7 calendar days ending at end-of-today. Drives the HBC Go dashboard
  // Today / Last 7 days toggle (mirrors the sibling pathology-results-panel range query).
  // Newest first so a stale visit from earlier in the week doesn't push today's check-ins
  // below the fold.
  getEncountersCheckedInLastNDays$(practiceTenantId: string, days: number): Observable<EncounterStatusVo[]> {
    const safeDays = Math.max(1, Math.floor(days));
    const startOfRange = moment()
      .startOf('day')
      .subtract(safeDays - 1, 'days')
      .toDate();
    const endOfRange = moment().endOf('day').toDate();
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection<EncounterStatusVo>(ENCOUNTER_ROOT, r =>
        r
          .where('EncounterLineItems.EncounterHeader.DateOfService', '>=', startOfRange)
          .where('EncounterLineItems.EncounterHeader.DateOfService', '<=', endOfRange)
      )
      .valueChanges()
      .pipe(
        map(encounters => encounters.filter(e => e?.Status !== 'Cancelled')),
        map(encounters =>
          encounters.map(e => ({
            ...e,
            PatientEventDetails: e.PatientEventDetails
              ? { ...e.PatientEventDetails, CheckInTime: fromTimestamp(e.PatientEventDetails.CheckInTime) }
              : e.PatientEventDetails,
            EncounterLineItems: e.EncounterLineItems
              ? {
                  ...e.EncounterLineItems,
                  EncounterHeader: e.EncounterLineItems.EncounterHeader
                    ? {
                        ...e.EncounterLineItems.EncounterHeader,
                        DateOfService: fromTimestamp(e.EncounterLineItems.EncounterHeader.DateOfService),
                      }
                    : e.EncounterLineItems.EncounterHeader,
                }
              : e.EncounterLineItems,
          }))
        ),
        map(encounters =>
          _.orderBy(encounters, [s => (s.EncounterLineItems?.EncounterHeader?.DateOfService as Date)?.valueOf() ?? 0], ['desc'])
        )
      );
  }

  onlyWithImportantInfo(encounters: any[]): any {
    // only text notes important to bring encounter to doctor's attention
    const withInfo = encounters.filter(e => {
      const encounter = convertFirestoreToEncounter(e);
      return (
        encounter.ClinicalNote?.trim()?.length > 0 ||
        encounter.SymptomQuestionAnswers?.filter(s => s.SymptomId == 'section-notes' && s.AnswerValue?.trim().length > 0)?.length > 0
      );
    });
    return withInfo;
  }

  checkIfEncounterStillActive(practiceTenantId: string, patientId: string, encounterId: string) {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection(ENCOUNTER_ROOT, r =>
        r.where('Status', 'in', ['Created', 'Updated', 'Saved']).where('PatientEventDetails.Patient.PatientId', '==', patientId)
      )
      .valueChanges()
      .pipe(
        first(),
        map(encounters => encounters?.filter(e => e.EncounterId === encounterId) || []),
        map(encounters => encounters.length > 0)
      );
  }

  loadFullEncounter(encounter: EncounterStatusVo, practiceTenantId: string): any {
    return (
      (encounter &&
        this.loadEncounterSubCollections(encounter, practiceTenantId).pipe(
          map(([symptomQuestions, symptomQuestionAnswers, lineItems, prescriptions]) => ({
            ...encounter,
            SymptomQuestions: symptomQuestions || encounter.SymptomQuestions,
            SymptomQuestionAnswers: symptomQuestionAnswers || encounter.SymptomQuestionAnswers,
            EncounterLineItems: {
              ...encounter.EncounterLineItems,
              LineItems: lineItems || encounter.EncounterLineItems.LineItems,
            },
            MedicationsPrescriptions: prescriptions || encounter.MedicationsPrescriptions,
          }))
        )) ||
      of(null)
    );
  }

  startCapturingEncounter(practiceId: string, patientId: string, encounter: EncounterStatusVo) {
    return this.saveEncounter(practiceId, patientId, encounter).pipe(mapTo(encounter));
  }

  loadEncounterSubCollections(encounter: EncounterStatusVo, practiceTenantId: string): any {
    // threshold limits: 120: SymptomQuestions, 240: SymptomQuestionAnswers, 50: EncounterLineItems.LineItems,
    return forkJoin([
      of(encounter).pipe(
        switchMap(s => {
          if (s.SymptomQuestions == null || s?.SymptomQuestions?.length >= 120) {
            return this.encounterRef(practiceTenantId, s.EncounterId).collection('SymptomQuestions').valueChanges().pipe(first());
          }
          return of(null);
        })
      ),
      of(encounter).pipe(
        switchMap(s => {
          if (s.SymptomQuestionAnswers == null || s.SymptomQuestionAnswers?.length >= 240) {
            return this.encounterRef(practiceTenantId, s.EncounterId).collection('SymptomQuestionAnswers').valueChanges().pipe(first());
          }
          return of(null);
        })
      ),
      of(encounter).pipe(
        switchMap(s => {
          if (s.EncounterLineItems.LineItems == null || s.EncounterLineItems.LineItems.length >= 50) {
            return this.encounterRef(practiceTenantId, s.EncounterId)
              .collection('EncounterLineItems.LineItems')
              .valueChanges()
              .pipe(first());
          }
          return of(null);
        })
      ),
      of(encounter).pipe(
        switchMap(s => {
          if (s.MedicationsPrescriptions === null) {
            return this.encounterRef(practiceTenantId, s.EncounterId).collection('MedicationsPrescriptions').valueChanges().pipe(first());
          }
          return of(null);
        })
      ),
    ]);
  }

  getEncountersRxHistory(
    practiceTenantId: string,
    patientId: string,
    type?: 'dispensed' | 'prescribed' | 'all' | null
  ): Observable<EncounterStatusVo[]> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection(ENCOUNTER_ROOT, r =>
        r.where('Status', 'in', ['Completed', 'Finalised', 'Corrected']).where('PatientEventDetails.Patient.PatientId', '==', patientId)
      )
      .valueChanges()
      .pipe(
        first(),
        switchMap((encounters: EncounterStatusVo[]) =>
          encounters.length > 0 ? forkJoin([...encounters.map(encounter => this.loadFullEncounter(encounter, practiceTenantId))]) : []
        ),
        map(encounters => encounters.map(encounter => convertFirestoreToEncounter(encounter))),
        map(encounters =>
          encounters.filter(
            (encounter: EncounterStatusVo) =>
              encounter.EncounterLineItems.LineItems.find(s => s.LineType == 'Medicine' || s.LineType == 'Consumable') != null ||
              encounter.MedicationsPrescriptions?.find(s => s.MedicationPrescriptionLines.length > 0 != null)
          )
        ),
        map(encounters => {
          if (type === 'dispensed') {
            return encounters.filter((encounter: EncounterStatusVo) =>
              encounter.EncounterLineItems.LineItems.find(s => s.LineType == 'Medicine' || s.LineType == 'Consumable')
            );
          } else if (type === 'prescribed') {
            return encounters.filter((encounter: EncounterStatusVo) =>
              encounter.MedicationsPrescriptions?.find(s => s.MedicationPrescriptionLines.length > 0 != null)
            );
          } else {
            return encounters;
          }
        })
      );
  }

  stockAddonEnabled(bpn): Observable<boolean> {
    // return of(false);
    // todo uncomment when stock feature would be ready for release (need consumable)
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('StockAddon')
      .doc('Stock')
      .valueChanges()
      .pipe(
        map(s => s?.IsActive || false),
        catchError(() => of(false)) // in case if no stock addon enabled
      );
  }

  getMedicineStockLevel(
    bpn: string,
    providerId: string,
    qty,
    nappiCode: string,
    branch: string
  ): Observable<NexResponse<DispenseResponse>> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.get<NexResponse<DispenseResponse>>(
          // NB don't set qty, handle remaining stock on our side
          `${this.loadBalancerUrl}/v1/stock/info/${bpn}/${providerId}/${nappiCode}/0/${branch}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              rejectUnauthorized: 'false',
            },
          }
        )
      )
    );
  }

  hasCompletedEncounter$(practiceTenantId: string, patientId: string): Observable<boolean> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection(ENCOUNTER_ROOT, r =>
        r
          .where('Status', 'in', ['Completed', 'Finalised', 'Corrected'])
          .where('PatientEventDetails.Patient.PatientId', '==', patientId)
          .limit(1)
      )
      .valueChanges()
      .pipe(
        first(),
        map(docs => docs.length > 0)
      );
  }

  getCompletedEncountersForPatient$(practiceTenantId: string, patientId: string): Observable<EncounterStatusVo[]> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection(ENCOUNTER_ROOT, r =>
        r.where('Status', 'in', ['Completed', 'Finalised', 'Corrected']).where('PatientEventDetails.Patient.PatientId', '==', patientId)
      )
      .valueChanges()
      .pipe(
        first(),
        // Sort newest-first in memory rather than via .orderBy('LastUpdated', 'desc'):
        // saveEncounter only writes LastUpdated when an encounter has >=50 line items,
        // and Firestore silently omits documents missing the orderBy field — so a normal
        // completed consult was dropped entirely, leaving the patient timeline empty even
        // though the home "Today's Consultations" panel (ordered by DateOfService) showed it.
        // Mirrors getLatestActiveEncounterForPatient$. CheckInTime is converted so the
        // timeline renders a real date, not a raw Firestore Timestamp.
        map(docs =>
          _.orderBy(
            (docs as EncounterStatusVo[]).map(e => ({
              ...e,
              PatientEventDetails: e.PatientEventDetails
                ? { ...e.PatientEventDetails, CheckInTime: fromTimestamp(e.PatientEventDetails.CheckInTime) }
                : e.PatientEventDetails,
            })),
            [d => (d.PatientEventDetails?.CheckInTime as Date)?.getTime() || 0],
            ['desc']
          ).slice(0, 50)
        )
      );
  }

  // Returns the most recent in-progress encounter for this patient from Firestore,
  // without the onlyWithImportantInfo content filter used by getActiveEncounterForPatient.
  // Used by HBC Go to resume an encounter after a page refresh.
  // Note: sorting is done in-memory rather than via Firestore .orderBy() to avoid
  // requiring a composite index. In-progress encounters per patient are bounded
  // (typically < 5) so this is fine.
  getLatestActiveEncounterForPatient$(practiceTenantId: string, patientId: string): Observable<EncounterStatusVo | null> {
    return this.firestore
      .collection(PRACTICE_ROOT)
      .doc(practiceTenantId)
      .collection(ENCOUNTER_ROOT, r =>
        r.where('Status', 'in', ['Created', 'Updated', 'Saved']).where('PatientEventDetails.Patient.PatientId', '==', patientId)
      )
      .valueChanges()
      .pipe(
        first(),
        map(docs => {
          if (!docs || docs.length === 0) {
            return null;
          }
          const sorted = _.orderBy(
            docs as EncounterStatusVo[],
            [d => fromTimestamp((d as any)?.PatientEventDetails?.CheckInTime)?.getTime() || 0],
            ['desc']
          );
          return convertFirestoreToEncounter(sorted[0]);
        })
      );
  }
}
